/* Instalación y actualización de la app.
 *
 * Dos cosas que el navegador expone y que conviene no dejar libradas a su
 * comportamiento por defecto:
 *
 *   · `beforeinstallprompt`: Chrome decide cuándo mostrar el cartel de
 *     instalar, y a veces no lo muestra nunca. Interceptándolo, el botón
 *     "Instalar" queda en la app y aparece cuando nosotros queremos.
 *
 *   · Actualizaciones: por defecto un service worker nuevo se queda esperando
 *     a que se cierren TODAS las pestañas, así que alguien que deja la app
 *     abierta toda la semana no ve un deploy nunca. Acá se le avisa.
 */

import { escriturasEnCurso } from "./supabase/escriturasEnCurso";

let promptGuardado = null;
const oyentes = new Set();

/* Actualizaciones: cuando hay una versión nueva esperando, NO se activa sola
 * (eso recargaba la página sin avisar). Se guarda la función que la aplica y se
 * avisa a la UI, que muestra un cartel para que la persona actualice cuando
 * quiera. */
let aplicarUpdate = null;
const oyentesUpdate = new Set();

export function hayActualizacionPendiente() {
  return !!aplicarUpdate;
}
/* Aplica la actualización esperando: activa el service worker nuevo, que al
 * tomar el control dispara el `controllerchange` de abajo y recarga. */
export function aplicarActualizacion() {
  if (aplicarUpdate) aplicarUpdate();
}
/* La UI se suscribe para enterarse de que hay una versión nueva. Devuelve la
 * baja. El oyente recibe `{ motivo }`: "deploy" para el aviso de siempre, o
 * uno de los de Aduana (ver `pedirActualizar`, abajo). */
export function alHaberActualizacion(fn) {
  oyentesUpdate.add(fn);
  return () => oyentesUpdate.delete(fn);
}
function avisarOyentes(info) {
  oyentesUpdate.forEach((fn) => {
    try {
      fn(info);
    } catch (e) {
      /* un oyente roto no puede tumbar a los demás */
    }
  });
}
function avisarUpdate(activar) {
  aplicarUpdate = activar;
  avisarOyentes({ motivo: "deploy" });
}

function avisar() {
  oyentes.forEach((fn) => {
    try {
      fn(estadoInstalacion());
    } catch (e) {
      /* un oyente roto no puede tumbar a los demás */
    }
  });
}

export function estadoInstalacion() {
  return {
    sePuedeInstalar: !!promptGuardado,
    yaInstalada:
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS no soporta display-mode y usa esta propiedad no estándar
      window.navigator.standalone === true,
  };
}

export function alCambiarInstalacion(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

export async function instalar() {
  if (!promptGuardado) return 'no-disponible';
  promptGuardado.prompt();
  const { outcome } = await promptGuardado.userChoice;
  // El prompt se consume: si lo rechazan, hay que esperar a que el navegador
  // vuelva a ofrecerlo. Guardarlo para reusar no funciona.
  promptGuardado = null;
  avisar();
  return outcome;
}

export function registrarPWA() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    promptGuardado = e;
    avisar();
  });

  window.addEventListener('appinstalled', () => {
    promptGuardado = null;
    avisar();
  });

  if (!('serviceWorker' in navigator)) return;

  // En desarrollo el service worker estorba: cachea el bundle y te hace dudar
  // de si el cambio salió o no.
  if (process.env.NODE_ENV !== 'production') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        reg.addEventListener('updatefound', () => {
          const nuevo = reg.installing;
          if (!nuevo) return;
          nuevo.addEventListener('statechange', () => {
            // controller presente = ya había una versión corriendo, o sea que
            // esto es una actualización y no la primera instalación.
            if (nuevo.state === 'installed' && navigator.serviceWorker.controller) {
              // Hay versión nueva esperando. No se activa sola: se avisa a la UI
              // y la persona decide cuándo (el cartel de actualización).
              avisarUpdate(() => {
                nuevo.postMessage('activar-ya');
              });
            }
          });
        });
      })
      .catch((err) => {
        // Que falle el registro no puede romper la app: la PWA es una mejora,
        // no un requisito para usarla.
        console.warn('[pwa] no se pudo registrar el service worker:', err);
      });

    // Si al cargar YA había un service worker al mando, un cambio de
    // controlador significa que se activó una versión nueva y hay que recargar
    // para tomarla. Si NO había ninguno, el cambio es la primera instalación
    // reclamando la página: recargar ahí sólo produce un parpadeo al entrar
    // por primera vez, sin ganar nada.
    const habiaControlador = !!navigator.serviceWorker.controller;
    let recargando = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!habiaControlador) return;
      if (recargando) return; // Chrome puede dispararlo dos veces
      recargando = true;
      window.location.reload();
    });
  });
}

/* ── LA VERSIÓN QUE CORRE CONTRA LA QUE ESTÁ PUBLICADA ───────────────────────
 *
 * Fabián, 09/10: emitió un MIC con la casilla 27 mal porque su pestaña era la
 * de la mañana. El arreglo se había publicado a las 11:15 y él nunca recargó.
 *
 * El cartel de arriba no lo podía salvar: se entera de una versión nueva por
 * el service worker, y el navegador sólo ve un worker nuevo cuando cambia
 * `sw.js`, que NO cambia con cada deploy (su `VERSION` se sube a mano, y la
 * última vez fue el 31/07, seiscientos PR atrás). Para el navegador no había
 * nada nuevo.
 *
 * Lo que sí cambia en CADA deploy es el nombre del paquete: CRA escribe
 * `main.<hash>.js` y el hash sale del contenido —incluida la lista de las
 * pantallas que se cargan aparte, así que un arreglo que toca sólo el MIC
 * también lo cambia—. Comparar el `main` que cargó esta pestaña con el que
 * nombra hoy el `index.html` publicado dice sin vueltas si la pestaña quedó
 * vieja. Es la misma comparación con la que el workflow de deploy espera a ver
 * publicado un build.
 *
 * Por eso NO se usa `registration.update()`: no vería el deploy de las 11:15
 * (`sw.js` es el mismo) y, cuando `sw.js` sí cambia, el worker nuevo se activa
 * solo al instalarse (`skipWaiting` en su `install`) y recarga la página por
 * el `controllerchange` de arriba en el momento que sea, con el formulario
 * lleno o vacío. Justo lo que acá se quiere controlar. */

/* Cuánto se espera, como mucho, a que conteste el `index.html`. Sin red la
 * consulta falla al toque; con red mala, a los 2,5 s se sigue con la versión
 * que hay y el cartel queda para después. */
export const ESPERA_VERSION = 2500;

const RE_PAQUETE = /\/static\/(?:js|css)\/main\.[0-9a-f]+\.(?:js|css)/g;

/* Los `main.<hash>.js|css` que nombra un texto, sin repetir y ordenados. */
export function paquetesDe(texto) {
  const vistos = new Set();
  for (const ruta of String(texto || "").match(RE_PAQUETE) || []) {
    vistos.add(ruta.slice(ruta.lastIndexOf("/") + 1));
  }
  return [...vistos].sort();
}

/* Los que cargó esta pestaña. En desarrollo el paquete se llama `bundle.js` y
 * en los tests no hay ninguno: da [] y no se compara nada — ni el modo
 * desarrollo ni jsdom salen nunca a buscar una versión. */
export function paquetesCargados(doc = typeof document === "undefined" ? null : document) {
  if (!doc) return [];
  const rutas = [...doc.querySelectorAll("script[src], link[rel='stylesheet'][href]")]
    .map((el) => el.getAttribute("src") || el.getAttribute("href") || "");
  return paquetesDe(rutas.join(" "));
}

/* ¿Es otra versión? Sólo si los dos lados nombran su `main.js` y no coinciden;
 * el CSS se compara si los dos lo tienen (un arreglo de impresión puede vivir
 * sólo en la hoja de estilos). Lo que no se pudo leer NO es una versión nueva:
 * un portal cautivo o un error del servidor no pueden mandar a nadie a
 * recargar. */
export function esOtraVersion(cargados, servidos) {
  const de = (lista, ext) => (lista || []).find((n) => n.endsWith(`.${ext}`)) || null;
  const jsAca = de(cargados, "js");
  const jsAlla = de(servidos, "js");
  if (!jsAca || !jsAlla) return false;
  if (jsAca !== jsAlla) return true;
  const cssAca = de(cargados, "css");
  const cssAlla = de(servidos, "css");
  return !!(cssAca && cssAlla && cssAca !== cssAlla);
}

/* Se puede mirar si esta pestaña sabe qué versión es (producción) y el
 * navegador tiene `fetch`, o si el service worker ya avisó una. */
export function sePuedeComprobarVersion() {
  return hayActualizacionPendiente()
    || (paquetesCargados().length > 0 && typeof fetch === "function");
}

async function traerIndex(senal) {
  /* `no-store` para que no conteste la caché del navegador. El service worker
     no se mete: no es una navegación ni un estático con hash (ver sw.js). */
  const r = await fetch(`${process.env.PUBLIC_URL || ""}/`, {
    cache: "no-store",
    signal: senal,
  });
  if (!r.ok) throw new Error(`index.html contestó ${r.status}`);
  return r.text();
}

/* La versión a la que se fue a buscar la última vez, para el seguro contra el
 * bucle (`tocaRecargarPorVersion`). */
let objetivoPendiente = null;

/* ¿Hay una versión nueva? Mira el `index.html` publicado, con tope de tiempo:
 * nunca tarda más de `espera`. Si la encuentra, la deja anotada como
 * actualización pendiente —el cartel de siempre la ve y su botón recarga—.
 *
 * Contesta `{ nueva, objetivo, fresca }`:
 *
 *   fresca: true   se leyó el `index.html` publicado AHORA y nombra su
 *                  `main.<hash>.js`. `nueva` dice si es otro que el de esta
 *                  pestaña; `objetivo` = los paquetes publicados.
 *   fresca: false  no se pudo leer (sin red, tarde, un portal del wifi, en
 *                  desarrollo o en los tests): se contesta lo que ya se sabía
 *                  de antes, que sirve para el cartel pero NO para recargar
 *                  sola. Si el servidor no contesta ahora, una recarga
 *                  tampoco llegaría: dejaría la pestaña colgada a medio
 *                  navegar, o en la página de error del navegador. */
export async function buscarVersionNueva({
  espera = ESPERA_VERSION,
  traer = traerIndex,
  cargados = paquetesCargados(),
} = {}) {
  const loQueSeSabia = () => (hayActualizacionPendiente()
    ? { nueva: true, objetivo: objetivoPendiente || "service-worker", fresca: false }
    : { nueva: false, objetivo: null, fresca: false });
  if (!cargados.length) return loQueSeSabia();

  const corte = typeof AbortController === "function" ? new AbortController() : null;
  let reloj = null;
  try {
    const texto = await Promise.race([
      traer(corte ? corte.signal : undefined),
      new Promise((_, mal) => {
        reloj = setTimeout(() => {
          if (corte) corte.abort();
          mal(new Error("el index.html tardó demasiado"));
        }, espera);
      }),
    ]);
    const servidos = paquetesDe(texto);
    /* Un `index.html` sin `main.<hash>.js` no es el nuestro (un portal
       cautivo, una página de error con 200): no se leyó nada. */
    if (!servidos.some((n) => n.endsWith(".js"))) return loQueSeSabia();
    if (!esOtraVersion(cargados, servidos)) {
      return { nueva: false, objetivo: null, fresca: true };
    }
    objetivoPendiente = servidos.join("|");
    /* Si el service worker ya había avisado una, su aviso sigue valiendo; si
       no, aplicarla es recargar: la navegación va por red primero (sw.js),
       así que recargar trae el `index.html` y el paquete nuevos. */
    if (!hayActualizacionPendiente()) avisarUpdate(() => window.location.reload());
    return { nueva: true, objetivo: objetivoPendiente, fresca: true };
  } catch (e) {
    /* sin red, tarde o una respuesta rara: se sigue con lo que se sabía */
  } finally {
    clearTimeout(reloj);
  }
  return loQueSeSabia();
}

/* ── QUE NO HAYA NADA A MEDIO CARGAR ───────────────────────────────────────
 *
 * Se le pregunta a quien ya lo sabe. Las pantallas que tienen trabajo sin
 * guardar escuchan `beforeunload` para frenar el cierre de la pestaña
 * (FiboSheets, Choferes autorizados, y el formulario abierto de CRT y MIC) —
 * y recargar es cerrarla. Se manda uno de prueba: si alguna lo frenaría,
 * también frena la recarga. No cierra nada, sólo pregunta; y cualquier
 * pantalla que mañana avise al cerrar queda cubierta sin tocar esto. Si el
 * navegador no deja ni preguntar, se contesta que sí hay algo: ante la duda,
 * no se recarga. */
export function hayAlgoSinGuardar() {
  try {
    const prueba = new Event("beforeunload", { cancelable: true });
    window.dispatchEvent(prueba);
    return prueba.defaultPrevented;
  } catch (e) {
    return true;
  }
}

/* ¿Está todo quieto? Nada que frene el cierre Y nada escribiendo en la base.
 *
 * Lo segundo es por lo que la pantalla de antes deja viajando al cerrarse: Mi
 * espacio manda el libro entero al salir, una foto se sigue subiendo. Al
 * entrar a CRT o MIC esa pantalla ya se desmontó —no frena nada—, pero su
 * pedido sigue en camino, y recargar lo cortaría (ver escriturasEnCurso.js). */
export function estaTodoQuieto() {
  return !hayAlgoSinGuardar() && escriturasEnCurso() === 0;
}

/* Espera, como mucho `tope` ms, a que esté todo quieto. Pide dos miradas
 * seguidas en quietud, a `cada` ms una de otra: entre dos pedidos encadenados
 * (un guardado que esperaba en la cola detrás de otro) el contador pasa un
 * instante por cero, y eso no es quietud. Contesta si se aquietó; `vigente`
 * corta la espera si la pantalla ya no está. */
export function esperarQuietud({ tope = 3000, cada = 150, vigente = () => true } = {}) {
  return new Promise((listo) => {
    const hasta = Date.now() + tope;
    let quietas = 0;
    const mirar = () => {
      if (!vigente()) { listo(false); return; }
      quietas = estaTodoQuieto() ? quietas + 1 : 0;
      if (quietas >= 2) { listo(true); return; }
      if (Date.now() >= hasta) { listo(false); return; }
      setTimeout(mirar, cada);
    };
    mirar();
  });
}

/* ── NI UN BUCLE ─────────────────────────────────────────────────────────
 *
 * Si después de recargar la pestaña sigue viendo la misma versión pendiente
 * (un worker trabado, una caché del CDN que todavía no se enteró), no vuelve a
 * recargar: queda el cartel. Se anota en sessionStorage a qué versión se fue
 * y cuándo; con la misma versión, o con menos de un minuto de la recarga
 * anterior, no toca. Si el navegador no deja anotar, tampoco: sin el seguro no
 * hay forma de garantizar que no se repita. */
const CLAVE_RECARGA_VERSION = "hub.recargaPorVersionNueva";

export function tocaRecargarPorVersion(objetivo, ahora = Date.now()) {
  try {
    const antes = JSON.parse(window.sessionStorage.getItem(CLAVE_RECARGA_VERSION) || "null");
    if (antes && (antes.objetivo === objetivo || ahora - Number(antes.cuando || 0) < 60000)) {
      return false;
    }
    window.sessionStorage.setItem(
      CLAVE_RECARGA_VERSION,
      JSON.stringify({ objetivo, cuando: ahora }),
    );
    return true;
  } catch (e) {
    return false;
  }
}

/* Toma la versión nueva: recarga.
 *
 * Una recarga simple alcanza: la navegación va por red primero (sw.js), así
 * que trae el `index.html` nuevo, y los paquetes con hash nuevo no están en
 * ninguna caché. No se activa el worker que espera ni se vuelve a recargar
 * «por las dudas»: cada recarga de más es otra ventana en la que la página
 * vieja sigue usable y la nueva puede llegar encima de lo que se esté
 * cargando.
 *
 * `destino` es a dónde volver —la misma pantalla, con `?ver=<id>` para caer en
 * el mismo papel—: se escribe en la barra ANTES de recargar, porque recargar
 * vuelve a la dirección que haya. */
export function actualizarAhora(destino) {
  try {
    if (destino) window.history.replaceState(window.history.state, "", destino);
  } catch (e) {
    /* sin destino se vuelve a la dirección de ahora, que también sirve */
  }
  window.location.reload();
}

/* Corta una recarga que ya salió y no llega (red mala en la frontera).
 *
 * Mientras la respuesta no llega, el navegador deja la página vieja viva y
 * usable; cuando llega, la reemplaza con lo que haya tipeado. Antes de
 * dibujar un formulario sobre una recarga en camino, se la corta: `stop()`
 * cancela la navegación pendiente. Corta también lo que la página esté
 * bajando en ese momento (una imagen, una lectura); por eso sólo se usa ahí. */
export function cortarRecarga() {
  try {
    if (typeof window.stop === "function") window.stop();
  } catch (e) {
    /* sin `stop` no hay cómo cortarla: queda el cartel */
  }
}

/* ── EL PAPEL QUE ESTÁ ABIERTO ───────────────────────────────────────────
 *
 * El botón del cartel de Aduana vuelve al papel que está a la vista, ya con
 * la versión nueva. «El que está a la vista» se pregunta al tocar el botón, no
 * al guardar: entre una cosa y la otra la persona pudo abrir otro. CRT y MIC
 * anotan acá el id del que tienen abierto (en el formulario o en «ver e
 * imprimir») y lo borran al cerrarlo o al irse de la pantalla. Devuelve la
 * baja. */
let papelAbierto = null;

export function anotarPapelAbierto(id) {
  const anotado = id === null || id === undefined || id === "" ? null : String(id);
  papelAbierto = anotado;
  return () => {
    if (papelAbierto === anotado) papelAbierto = null;
  };
}

/* A dónde recargar: la pantalla de ahora con el papel abierto, o la dirección
 * tal cual si no hay ninguno. */
export function destinoParaActualizar() {
  if (typeof window === "undefined") return null;
  const { pathname, search } = window.location;
  return papelAbierto !== null
    ? `${pathname}?ver=${encodeURIComponent(papelAbierto)}`
    : `${pathname}${search}`;
}

/* Le pide al cartel que se muestre con un motivo de Aduana, aunque la persona
 * lo hubiera cerrado:
 *
 *   "antes-de-emitir"  al entrar a CRT o MIC había versión nueva pero no se
 *                      pudo recargar sola (algo a medio cargar, sin red, o
 *                      ya se probó).
 *   "tras-guardar"     se acaba de guardar o emitir con la versión vieja.
 *
 * No cambia CÓMO se aplica la actualización: eso ya quedó anotado. */
export function pedirActualizar(motivo) {
  avisarOyentes({ motivo });
}

/* DESPUÉS DE GUARDAR O EMITIR UN CRT O UN MIC (Igna, 09/10: «así no se emite
 * un papel con una versión vieja»). Guardar no se frena nunca —lo que la
 * persona cargó vale más que la versión con que lo cargó—, pero si la pestaña
 * quedó vieja se le dice en el momento, y el botón del cartel la deja en el
 * papel abierto (`destinoParaActualizar`) ya con la versión nueva, para
 * revisarlo antes de imprimir. Se llama sin `await`: no demora nada ni tira
 * errores. */
export function avisarTrasGuardar() {
  return buscarVersionNueva()
    .then(({ nueva }) => {
      if (nueva) pedirActualizar("tras-guardar");
      return nueva;
    })
    .catch(() => false);
}
